import React, { useState } from 'react';

import { useCollection } from '@amzn/awsui-collection-hooks';
import { Box, Button, Header, Modal, Pagination, SpaceBetween, Table } from '@amzn/awsui-components-react-v3';

import { useMetrics } from 'appSelectorContext';

import { METRIC_PUBLISHER_NAMES } from '../../constants';
import type { MPNToIPNMapping, MPNToIPNPair } from '../../store/types';
import { sortByLastThree } from '../Utils/Utils';
import { MPN_IPN_SELECTED_TOWNSEND } from './constants';

interface MPNToIPNMappingProps {
  mpnToIPNMapping: MPNToIPNMapping;
  onDismiss?: () => void;
  onConfirm?: (model: any) => void;
}

export const MPNToIPNMappings = ({
  mpnToIPNMapping,
  onDismiss = () => {},
  onConfirm = () => {},
}: MPNToIPNMappingProps) => {
  const context = useMetrics();
  const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(METRIC_PUBLISHER_NAMES.SCAN_MPN);
  const [visible, setVisible] = useState(true);
  const [selectedItems, setSelectedItems] = useState<MPNToIPNPair[]>([]);

  const closeModal = () => {
    setVisible(false);
    onDismiss();
  };

  const confirmSelection = () => {
    setVisible(false);
    onConfirm(selectedItems[0]);
    publisher.publishCounterMonitor(MPN_IPN_SELECTED_TOWNSEND, 1);
  };

  const sortByIPN = (elem1: MPNToIPNPair, elem2: MPNToIPNPair) => sortByLastThree(elem1, elem2, 'ipn');
  const associatedIpns = Array.isArray(mpnToIPNMapping?.associatedIpns) ? [...mpnToIPNMapping?.associatedIpns] : [];

  // pagination props
  const { items, paginationProps } = useCollection(associatedIpns?.sort(sortByIPN), {
    pagination: { pageSize: 25 },
    sorting: {},
  });

  return (
    <Box>
      <Modal
        onDismiss={closeModal}
        visible={visible}
        size="large"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" ariaLabel="Cancel IPN Selection dialog" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                ariaLabel="Confirm selected IPN"
                disabled={selectedItems.length === 0}
                onClick={confirmSelection}
              >
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={
          <Header
            variant="h2"
            actions={null}
            description="Choose internal part number (IPN) from the packing slip, case or component that corresponds to right manufacturing part number (MPN) and/or compare against APN Description."
          >
            Multiple IPN's found in Townsend - {mpnToIPNMapping?.mpn}
          </Header>
        }
      >
        <Table
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          selectedItems={selectedItems}
          ariaLabels={{
            selectionGroupLabel: 'IPNS selection',
            allItemsSelectionLabel: ({ selectedItems }) =>
              `${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} selected`,
            itemSelectionLabel: ({ selectedItems }, item) => {
              const isItemSelected = selectedItems.find((i) => i.ipn === item.ipn);
              return `${item.ipn} is ${isItemSelected ? '' : 'not'} selected`;
            },
          }}
          columnDefinitions={[
            {
              id: 'ipn',
              header: 'Amazon part number',
              cell: (e: MPNToIPNPair) => e.ipn,
            },
            {
              id: 'description',
              header: 'Amazon part number description',
              cell: (e: MPNToIPNPair) => e.description,
            },
          ]}
          pagination={<Pagination {...paginationProps} />}
          items={items}
          loadingText="Loading resources"
          selectionType="single"
          onRowClick={({ detail }: any) => {
            setSelectedItems([detail.item]);
          }}
          visibleColumns={['ipn', 'description']}
        />
      </Modal>
    </Box>
  );
};
